import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type DevicePreferences } from "@centvox/shared/media";
import { Banner, Button, VisuallyHidden } from "../../ui/primitives.js";
import { useI18n } from "../../i18n/react.js";
import { startMicLevelMeter } from "../../lib/mic-level.js";
import {
  acquireUserMedia,
  classifyMediaError,
  type MediaFailure,
} from "../../lib/acquire-media.js";
import { reconcileDevices } from "../../lib/prefs.js";
import { detectPlatform } from "../../lib/platform.js";

/**
 * **Green room** — katılım öncesi cihaz kontrolü (Rapor 15 F1-32).
 *
 * ### ⭐ Neden katılım ekranının PARÇASI?
 * Ayrı bir adım koymak "Katıl"a basmakla odaya girmek arasına ikinci bir
 * tıklama sokar. Tek ekran hem hedefi (ilk uzak kare <3 sn, F1-33) korur hem
 * de kullanıcı adını yazarken izin isteminin cevaplanmasını sağlar.
 *
 * ### ⚠️ `MediaEngine` BURADA KULLANILMAZ
 * Motor kritik yol dışıdır ve `getUserMedia` izniyle **paralel** yüklenir
 * (Rapor 14 §3.3). Önizleme ham `navigator.mediaDevices` ile yapılır; ESLint
 * kuralı 4 zaten `routes/join/**` içinde `media/**` import'unu yasaklar.
 *
 * ### ⚠️ Cihaz etiketleri izinden ÖNCE BOŞTUR
 * `enumerateDevices()` izin verilmeden `label: ""` döner. Bu yüzden liste
 * **izin alındıktan sonra** yenilenir; aksi hâlde kullanıcı "Cihaz 1 / Cihaz 2"
 * arasından seçim yapmak zorunda kalır.
 */
export function GreenRoom(props: {
  prefs: DevicePreferences;
  onChange: (prefs: DevicePreferences) => void;
}): JSX.Element {
  const i18n = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const meterRef = useRef<{ stop(): void } | null>(null);

  const [devices, setDevices] = useState<{
    audioIn: MediaDeviceInfo[];
    videoIn: MediaDeviceInfo[];
  }>({ audioIn: [], videoIn: [] });
  /**
   * ⚠️ Durum artık ikili DEĞİL. "İzin verilmedi" ile "kamera başka uygulamada"
   *    aynı ekranı gösterirse kullanıcı olmayan bir ayarı aramaya gider —
   *    mobilde tam olarak bu yaşandı.
   */
  const [failure, setFailure] = useState<MediaFailure | "none" | "pending">("pending");

  const { prefs, onChange } = props;

  /** Önceki akışı **tamamen** durdurur — kamera ışığı ancak böyle söner. */
  const teardown = useCallback((): void => {
    meterRef.current?.stop();
    meterRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const open = async (): Promise<void> => {
      teardown();
      try {
        /**
         * ⭐ Gevşetme merdiveni: kaydedilmiş cihaz kimliği geçersizse
         *    (`OverconstrainedError`) kimlik olmadan bir kez daha denenir
         *    (bkz. `lib/acquire-media.ts`).
         */
        const { stream, relaxed } = await acquireUserMedia(prefs);
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setFailure("none");

        /**
         * ⚠️ Ölü cihaz kimliği **kalıcı olarak** temizlenir. Yapılmazsa her
         *    açılışta aynı başarısız deneme tekrarlanır ve kullanıcı her
         *    seferinde bir kare gecikme yaşar.
         */
        if (relaxed) onChange({ ...prefs, audioDeviceId: null, videoDeviceId: null });

        // ⚠️ Önizlemede YALNIZ video: ses de bağlanırsa kullanıcı kendi sesini
        //    hoparlörden duyar ve geri besleme çığlığı oluşur.
        const video = videoRef.current;
        if (video) video.srcObject = new MediaStream(stream.getVideoTracks());

        const mic = stream.getAudioTracks()[0];
        const bar = barRef.current;
        if (mic && bar) meterRef.current = startMicLevelMeter(mic, bar);

        // Etiketler artık dolu → listeyi yenile.
        const list = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const next = {
          audioIn: list.filter((d) => d.kind === "audioinput"),
          videoIn: list.filter((d) => d.kind === "videoinput"),
        };
        setDevices(next);

        // Kayıtlı cihaz artık takılı değilse varsayılana düş (ölü `exact` koruması).
        const fixed = reconcileDevices(prefs, next);
        if (
          fixed.audioDeviceId !== prefs.audioDeviceId ||
          fixed.videoDeviceId !== prefs.videoDeviceId
        ) {
          onChange(fixed);
        }
      } catch (e) {
        if (cancelled) return;
        /**
         * 🚨 **"NotFoundError dışındaki her şey = izin reddi" YANLIŞTI.**
         *
         * Mobilde `OverconstrainedError` (kaydedilmiş cihaz kimliği artık
         * geçersiz) ve `NotReadableError` (kamerayı başka bir uygulama tutuyor)
         * bu dala düşüyor ve kullanıcıya **izin vermediği** söyleniyordu —
         * oysa izni vermişti. Artık her neden kendi mesajını alır.
         */
        setFailure(classifyMediaError(e));
      }
    };

    void open();
    return () => {
      cancelled = true;
      teardown();
    };
    // ⚠️ Yalnız cihaz kimliği ve kamera açıklığı akışı YENİDEN kurar. `mic`
    //    değişimi track'i etkilemez (`enabled` bayrağıdır) → gereksiz izin
    //    istemi ve kamera yanıp sönmesi olmaz.
  }, [prefs.audioDeviceId, prefs.videoDeviceId, prefs.cam, teardown, onChange, prefs]);

  // Cihaz hot-plug: kulaklık takıldığında liste anında güncellenir.
  useEffect(() => {
    const onDeviceChange = (): void => {
      void navigator.mediaDevices.enumerateDevices().then((list) =>
        setDevices({
          audioIn: list.filter((d) => d.kind === "audioinput"),
          videoIn: list.filter((d) => d.kind === "videoinput"),
        }),
      );
    };
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
  }, []);

  const platform = detectPlatform();

  return (
    <section data-green-room aria-label={i18n.t("green.title")}>
      <div data-preview>
        {/*
          ⚠️ `muted` ZORUNLUDUR: kendi sesini duymak geri besleme üretir.
          ⚠️ `playsInline` olmadan iOS videoyu tam ekrana zorlar.
        */}
        <video ref={videoRef} autoPlay playsInline muted aria-label={i18n.t("green.camera")} />
        {!prefs.cam ? <p data-preview-off>{i18n.t("green.joinWithCameraOff")}</p> : null}
      </div>

      <div data-level-meter aria-hidden="true">
        <i ref={barRef} />
      </div>
      <VisuallyHidden>{i18n.t("green.speakNow")}</VisuallyHidden>

      {failure === "denied" ? (
        <Banner level="warn">{i18n.t("green.permissionDenied")}</Banner>
      ) : failure === "busy" ? (
        <Banner level="warn">{i18n.t("green.deviceBusy")}</Banner>
      ) : failure === "unsupported" ? (
        <Banner level="warn">{i18n.t("green.deviceUnsupported")}</Banner>
      ) : failure === "unknown" ? (
        <Banner level="warn">{i18n.t("green.deviceFailed")}</Banner>
      ) : null}

      <div data-device-row>
        <DeviceSelect
          label={i18n.t("green.microphone")}
          empty={i18n.t("green.noMicrophone")}
          devices={devices.audioIn}
          value={prefs.audioDeviceId}
          onSelect={(id) => onChange({ ...prefs, audioDeviceId: id })}
        />
        <DeviceSelect
          label={i18n.t("green.camera")}
          empty={i18n.t("green.noCamera")}
          devices={devices.videoIn}
          value={prefs.videoDeviceId}
          onSelect={(id) => onChange({ ...prefs, videoDeviceId: id })}
        />
      </div>

      <div data-toggle-row>
        <Button
          pressed={!prefs.mic}
          onClick={() => onChange({ ...prefs, mic: !prefs.mic })}
          variant={prefs.mic ? "secondary" : "danger"}
        >
          {prefs.mic ? i18n.t("green.joinMuted") : i18n.t("room.micOn")}
        </Button>
        {/* ⚠️ iOS'ta ekran paylaşımı yoktur ama kamera vardır — bu düğme kalır. */}
        <Button
          pressed={!prefs.cam}
          onClick={() => onChange({ ...prefs, cam: !prefs.cam })}
          variant={prefs.cam ? "secondary" : "danger"}
        >
          {prefs.cam ? i18n.t("green.joinWithCameraOff") : i18n.t("room.camOn")}
        </Button>
      </div>

      {platform.suspendsMediaInBackground ? (
        <p data-hint>{i18n.t("status.backgroundNotice")}</p>
      ) : null}
    </section>
  );
}

/**
 * ⚠️ Cihaz **yoksa** açılır liste gizlenmez, "bulunamadı" yazar. Boş bir liste
 *    kullanıcıya arayüzün bozuk olduğunu düşündürür.
 */
function DeviceSelect(props: {
  label: string;
  empty: string;
  devices: MediaDeviceInfo[];
  value: string | null;
  onSelect: (deviceId: string | null) => void;
}): JSX.Element {
  if (props.devices.length === 0) {
    return (
      <p data-hint>
        {props.label}: {props.empty}
      </p>
    );
  }
  return (
    <label data-device-select>
      <span>{props.label}</span>
      <select
        value={props.value ?? ""}
        onChange={(e) => props.onSelect(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">{props.label}</option>
        {props.devices.map((d, i) => (
          <option key={d.deviceId} value={d.deviceId}>
            {/* ⚠️ İzin verilmeden etiket boştur; sıra numarası yedeği kalır. */}
            {d.label === "" ? `${props.label} ${i + 1}` : d.label}
          </option>
        ))}
      </select>
    </label>
  );
}
